/**
 * PILOTO CUBIERTA POR TIPO (MADERINCO-SELECCION-POR-TIPO-T1; arbol de Atenea v0.1; skill front-panel-claridad v0.3).
 * Sin DOM, para el check.
 *
 * Reglas que esto hace cumplir (seleccion-guiada.md y WORK-CUBIERTA-PREGUNTAS-v01):
 *   - Q1 filtra por la TABLA curada (arbolCubierta.js), por id del catalogo (fuenteId), nunca por palabras.
 *   - Variantes de libros distintos quedan separadas, cada una con su unidad y su alcance.
 *   - Agregar exige una cantidad POSITIVA ESCRITA: la de referencia no reemplaza en silencio un dato vacio o invalido.
 *   - Q2 es obligatoria para dar el conjunto por registrado; "incluida" en un compuesto + "falta presupuestarla" =
 *     posible doble conteo: se detiene. Nunca se anuncia una solucion tecnica completa.
 *   - Q3 abre complementos SOLO entre los que Atenea reviso; abrir no agrega.
 *   - Cambiar Q1 invalida Q2/Q3 y pide revisar lo ya agregado, sin borrarlo.
 *
 * @module application/presupuestos/seleccionCubierta
 */
import { TIPOS, COMPLEMENTOS, Q3 } from './arbolCubierta.js';
import { parsearNumeroColombiano } from '../../domain/value-objects/numeroColombiano.js';

const normal = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** fuenteId -> { seccion, item } de la plantilla servida. */
export function indicePorFuente(plantilla) {
  const m = new Map();
  for (const s of plantilla?.secciones || []) for (const it of s.items || []) if (it.fuenteId) m.set(it.fuenteId, { seccion: s, item: it });
  return m;
}

/** Las claves de las secciones que hospedan algun id del arbol (tipo o complemento): se navegan como UNA entrada. */
export function seccionesDeCubierta(plantilla) {
  const ids = new Set([...TIPOS.flatMap((t) => t.variantes.map((v) => v.fuenteId)), ...COMPLEMENTOS]);
  return new Set((plantilla?.secciones || []).filter((s) => (s.items || []).some((it) => ids.has(it.fuenteId))).map((s) => s.clave));
}

/** Los candidatos de un tipo: cada variante del arbol con su item servido (o en faltantes si la plantilla no lo trae). */
export function candidatosDe(plantilla, codigo) {
  const tipo = TIPOS.find((t) => t.codigo === codigo);
  if (!tipo) return { candidatos: [], faltantes: [] };
  const idx = indicePorFuente(plantilla);
  const candidatos = [];
  const faltantes = [];
  for (const v of tipo.variantes) {
    const s = idx.get(v.fuenteId);
    if (s) candidatos.push({ variante: v, seccion: s.seccion, item: s.item });
    else faltantes.push(v.fuenteId);
  }
  return { candidatos, faltantes };
}

/** Los complementos de una opcion de Q3, solo entre los revisados por Atenea. "ninguno" = []. */
export function complementosDe(plantilla, codigoQ3) {
  const op = Q3.find((o) => o.codigo === codigoQ3);
  if (!op || !op.palabras.length) return [];
  const idx = indicePorFuente(plantilla);
  return COMPLEMENTOS.map((id) => idx.get(id)).filter(Boolean)
    .filter(({ item }) => op.palabras.some((p) => normal(item.descripcion).includes(p)));
}

/** La cantidad escrita, si es positiva; si no, null (nunca la de referencia en silencio). */
export function cantidadExplicita(texto) {
  const q = parsearNumeroColombiano(String(texto ?? '').trim());
  return Number.isFinite(q) && q > 0 ? q : null;
}

const ALCANCE_COMPUESTO = 'compuesto_con_estructura_explicita';

/**
 * El estado del conjunto Cubierta. st = { tipo, agregados: [{fuenteId, alcance}], q2, q3 }.
 * codigos: sin_tipo | tipo_no_definido | pendiente_q2 | doble_conteo | pendiente | registrado (nunca "completo").
 */
export function estadoDelConjunto(st) {
  const s = st || {};
  if (!s.tipo) return { codigo: 'sin_tipo', frase: 'Elige el tipo de cubierta.' };
  if (s.tipo === 'no_definido') return { codigo: 'tipo_no_definido', frase: 'Tipo todavia no definido: queda como borrador.' };
  const compuesto = (s.agregados || []).some((a) => a.alcance === ALCANCE_COMPUESTO);
  if (!s.q2) return { codigo: 'pendiente_q2', frase: 'Falta decir como queda resuelta la estructura (pregunta 2).' };
  if (s.q2 === 'falta' && compuesto) return { codigo: 'doble_conteo', frase: 'Revisar: la cubierta agregada ya incluye estructura y pediste presupuestarla aparte (posible doble conteo).' };
  if (s.q2 === 'no_definido' || s.q2 === 'existente') return { codigo: 'pendiente', frase: 'Estructura pendiente de comprobar: el conjunto queda pendiente.' };
  if (s.q2 === 'incluida' && !compuesto) return { codigo: 'pendiente', frase: 'El alcance de esta variante esta por confirmar: revisa el detalle antes de dar la estructura por incluida.' };
  return { codigo: 'registrado', frase: 'Seleccion registrada. No es una solucion tecnica validada: la revisa el ingeniero.' };
}

/** Cambiar Q1: Q2 y Q3 dejan de valer y, si ya habia partidas, se marcan para revisar (no se borran). */
export function cambiarTipo(st, nuevo) {
  const s = st || { agregados: [] };
  return { ...s, tipo: nuevo, q2: null, q3: null, revisar: (s.agregados || []).length > 0 && s.tipo !== nuevo };
}
